import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { formatOrgNr } from '@nk/company-block';
import {
  CompanyPersonSearchWithDistanceAndSearchType,
  SearchQueryMethod,
  SimpleSearchEntity,
  SimpleSearchWithDistanceEntity,
} from '@nk/data-entities-lib';
import { NkChanges, titleCaseCompanyName, toTitleCase } from '@nk/utils-lib';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CompanyPersonSearchOptionComponent } from '../company-person-search-option/company-person-search-option.component';

interface OrderedListBySearchType {
  all: CompanyPersonSearchWithDistanceAndSearchType[];
  fuzzy: CompanyPersonSearchWithDistanceAndSearchType[];
  quickCompanies: CompanyPersonSearchWithDistanceAndSearchType[];
  quickPerson: CompanyPersonSearchWithDistanceAndSearchType[];
}

@Component({
  selector: 'nk-company-person-search-results',
  imports: [AsyncPipe, TranslateModule, CompanyPersonSearchOptionComponent],
  templateUrl: './company-person-search-results.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CompanyPersonSearchResultsComponent implements OnChanges, OnInit {
  @Input() public aggregatedSimpleSearchResults: CompanyPersonSearchWithDistanceAndSearchType[] = [];
  @Input() public selectedItemIndex: number | undefined;
  @Input() public openDropdown = false;
  @Input() public disableEnterKey = false;
  @Input() public query = ''; // used for highlighting.
  @Output() public searchSelect = new EventEmitter<SimpleSearchEntity>();

  private _searchResultChanges = new BehaviorSubject<CompanyPersonSearchWithDistanceAndSearchType[]>([]);
  public $searchResultChanges = this._searchResultChanges.asObservable();
  public orderedListByType$!: Observable<OrderedListBySearchType>;

  constructor(private _translateService: TranslateService) {}

  public ngOnInit(): void {
    this.orderedListByType$ = this.$searchResultChanges.pipe(
      map((res) => {
        res = this.formatSearchResults(res);
        const quickPerson = res.filter(
          (a) => a.searchType === SearchQueryMethod.quick && a.search_target_type === 'person',
        );
        const quickCompanies = res.filter(
          (a) => a.searchType === SearchQueryMethod.quick && a.search_target_type !== 'person',
        );

        // Ensures that Other don't contain the person or company in the first set. (quick comes first)
        const fuzzy = res.filter((b) => b.searchType === SearchQueryMethod.fuzzy);

        const orderedListByType: OrderedListBySearchType = {
          quickCompanies: quickCompanies,
          quickPerson: quickPerson,
          fuzzy: fuzzy,
          all: res,
        };
        return orderedListByType;
      }),
    );
  }

  public formatSearchResults(
    simpleSearchResults: CompanyPersonSearchWithDistanceAndSearchType[],
  ): CompanyPersonSearchWithDistanceAndSearchType[] {
    const previousPrefixTranslation = this._translateService.instant(
      extractTranslation('simple_search.aux_label_company_alias'),
    ) as string;
    const parentPrefixTranslation = this._translateService.instant(
      extractTranslation('simple_search.aux_label_company_child'),
    ) as string;

    return simpleSearchResults.map((searchResult, index) =>
      formatLabels(searchResult, previousPrefixTranslation, parentPrefixTranslation, index),
    );
  }

  public ngOnChanges(changes: NkChanges<CompanyPersonSearchResultsComponent>): void {
    if (changes.aggregatedSimpleSearchResults) {
      this._searchResultChanges.next(this.aggregatedSimpleSearchResults);
    }
  }

  public onItemClick(item: SimpleSearchWithDistanceEntity): void {
    this.searchSelect.emit(item);
  }
}

function formatLabels(
  item: CompanyPersonSearchWithDistanceAndSearchType,
  previousPrefix: string,
  parentPrefix: string,
  index: number,
): CompanyPersonSearchWithDistanceAndSearchType {
  let inputSearchLabelAux = item.search_label_aux;

  if (item.search_target_type !== 'person') {
    inputSearchLabelAux = formatCompanyLabel(item);
  }

  const { searchLabel, searchLabelAux } = getFormattedLabels(item, inputSearchLabelAux, previousPrefix, parentPrefix);
  return {
    ...item,
    search_label: searchLabel,
    search_label_aux: searchLabelAux,
    itemIndex: index,
  };
}

function getFormattedLabels(
  item: CompanyPersonSearchWithDistanceAndSearchType,
  searchLabelAux: string,
  previousPrefix: string,
  parentPrefix: string,
): {
  searchLabel: string;
  searchLabelAux: string;
} {
  switch (item.search_target_type) {
    case 'company':
      return {
        searchLabel: titleCaseCompanyName(item.search_label) || '',
        searchLabelAux,
      };
    case 'org_nr':
      return {
        searchLabel: titleCaseCompanyName(item.search_label) || '',
        searchLabelAux,
      };
    case 'company_alias':
      return {
        searchLabel: titleCaseCompanyName(item.search_label) || '',
        searchLabelAux: searchLabelAux + ` (${previousPrefix} ${titleCaseCompanyName(item.search_target)})`,
      };
    case 'company_child':
      return {
        searchLabel: titleCaseCompanyName(item.search_label_aux) || '',
        searchLabelAux: `(${parentPrefix} ${titleCaseCompanyName(item.search_label)})`,
      };
    default:
      return {
        searchLabel: toTitleCase(item.search_label),
        searchLabelAux,
      };
  }
}

function formatCompanyLabel(item: CompanyPersonSearchWithDistanceAndSearchType): string {
  let searchLabelAux = item.search_label_aux;
  const orgNrSchema = item.search_label_aux.substring(0, 2);
  if (['NO', 'DK', 'FI', 'SE'].includes(orgNrSchema)) {
    const orgnr = item.search_label_aux.substring(2, item.search_label_aux.length);
    searchLabelAux = formatOrgNr(orgnr, orgNrSchema);
  }
  return searchLabelAux;
}
